"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useCallback, Suspense } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { Slug } from "@/content/types";
import type { SanityImageSource } from "@/components/atoms/SanityImage";
import Link from "next/link";
import { SanityImage } from "@/components/atoms/SanityImage";
import { getDocumentUrl } from "@/lib/routing";
import { generateLayeredGradients } from "@/lib/gradients";
import { PaginationControls } from "@/components/molecules/PaginationControls";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
type HeroGradientInfo = {
  variant?: string;
  gradientColors?: string[];
  heroLogo?: SanityImageSource;
};
type CardItem = {
  _id: string;
  _type: string;
  slug?: Slug | null;
  title?: string;
  image?: SanityImageSource | null;
  subtitle?: string;
  heroGradient?: HeroGradientInfo | null;
};

/**
 * Maps column count to flex-basis values for responsive item widths.
 * Accounts for gap-8 (2rem) between items: width = (100% - (N-1)*2rem) / N
 */
const ITEM_WIDTH_MAP: Record<number, string> = {
  1: "w-full",
  2: "w-full md:w-[calc((100%-2rem)/2)]",
  3: "w-full md:w-[calc((100%-4rem)/3)]",
  4: "w-full md:w-[calc((100%-6rem)/4)]"
};
interface ReferenceGridClientProps {
  cardItems: CardItem[];
  cols?: number;
  cardBackgroundColor?: string;
  enablePagination?: boolean;
  itemsPerPage?: number;
  gridId?: string;
}
interface ReferenceCardProps {
  item: CardItem;
  cardBackgroundColor?: string;
}
const ReferenceCard = t0 => {
  const $ = _c(36);
  const {
    item,
    cardBackgroundColor
  } = t0;
  const heroGradient = item.heroGradient;
  const hasGradient = heroGradient?.gradientColors?.length && heroGradient?.heroLogo;
  let t1;
  if ($[0] !== hasGradient || $[1] !== heroGradient) {
    t1 = hasGradient ? generateLayeredGradients(heroGradient.gradientColors) : undefined;
    $[0] = hasGradient;
    $[1] = heroGradient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const gradientStyle = t1;
  const imageWithAlt = item.image as SanityImageSource;
  let t2;
  if ($[3] !== gradientStyle) {
    t2 = gradientStyle ? {
      backgroundImage: gradientStyle
    } : undefined;
    $[3] = gradientStyle;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const cardGradientStyle = t2;
  let t3;
  if ($[5] !== heroGradient) {
    t3 = heroGradient?.gradientColors?.[0] ? {
      backgroundColor: heroGradient.gradientColors[0]
    } : undefined;
    $[5] = heroGradient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const glowBackgroundStyle = t3;
  const t4 = item.slug?.current;
  let t5;
  if ($[7] !== item._type || $[8] !== t4) {
    t5 = getDocumentUrl(item._type, t4);
    $[7] = item._type;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = item.title;
  let t7;
  if ($[10] !== cardBackgroundColor) {
    t7 = cn("section-aq-white flex flex-col w-full overflow-hidden rounded-2xl border border-white/10 bg-background shadow-xl transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-2xl", cardBackgroundColor);
    $[10] = cardBackgroundColor;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== cardGradientStyle || $[13] !== glowBackgroundStyle || $[14] !== gradientStyle || $[15] !== hasGradient || $[16] !== heroGradient || $[17] !== imageWithAlt || $[18] !== item.image) {
    t8 = hasGradient && gradientStyle ? <Box className="relative w-full overflow-hidden aspect-video bg-black flex items-center justify-center" style={cardGradientStyle}><Box className="relative flex items-center justify-center p-4"><Box className="absolute inset-0 scale-130 blur-2xl opacity-80 rounded-full" style={glowBackgroundStyle} aria-hidden="true" /><SanityImage image={heroGradient.heroLogo as SanityImageSource} width={200} placeholder="empty" className="relative max-h-20 w-auto object-contain" sizes="200px" /></Box></Box> : item.image ? <Box className="relative w-full overflow-hidden aspect-video"><SanityImage image={imageWithAlt} fill={true} aspectRatio="16/9" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" className="absolute inset-0 object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-110" /><Box className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" /></Box> : null;
    $[12] = cardGradientStyle;
    $[13] = glowBackgroundStyle;
    $[14] = gradientStyle;
    $[15] = hasGradient;
    $[16] = heroGradient;
    $[17] = imageWithAlt;
    $[18] = item.image;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== item.title) {
    t9 = <Text as="p" className="text-xl font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors duration-300">{item.title}</Text>;
    $[20] = item.title;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== item.subtitle) {
    t10 = item.subtitle && <Text as="p" className="mt-4 text-sm text-muted-foreground line-clamp-3">{item.subtitle}</Text>;
    $[22] = item.subtitle;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <Box className="p-6 flex flex-col justify-between flex-grow">{t9}{t10}</Box>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Box className="px-6 pb-6 mt-auto"><Box className="h-1 w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full group-hover:shadow-[var(--aq-shadow-glow-orange-subtle)]" /></Box>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t7 || $[30] !== t8) {
    t13 = <Box className={t7}>{t8}{t11}{t12}</Box>;
    $[28] = t11;
    $[29] = t7;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== item.title || $[33] !== t13 || $[34] !== t5) {
    t14 = <Link href={t5} aria-label={t6} className="group flex h-full">{t13}</Link>;
    $[32] = item.title;
    $[33] = t13;
    $[34] = t5;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
const ReferenceGridClientInner = t0 => {
  const $ = _c(36);
  const {
    cardItems,
    cols: t1,
    cardBackgroundColor,
    enablePagination: t2,
    itemsPerPage: t3,
    gridId: t4
  } = t0;
  const cols = t1 === undefined ? 3 : t1;
  const enablePagination = t2 === undefined ? false : t2;
  const itemsPerPage = t3 === undefined ? 12 : t3;
  const gridId = t4 === undefined ? "grid" : t4;
  const itemWidthClass = ITEM_WIDTH_MAP[cols] || ITEM_WIDTH_MAP[3];
  const searchParams = useSearchParams();
  const router = useRouter();
  const gridRef = useRef(null);
  const pageParamKey = gridId === "grid" ? "page" : `${gridId}-page`;
  let t5;
  if ($[0] !== pageParamKey || $[1] !== searchParams) {
    t5 = searchParams.get(pageParamKey);
    $[0] = pageParamKey;
    $[1] = searchParams;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const pageParam = t5;
  const currentPage = Math.max(1, parseInt(pageParam || "1", 10));
  const totalPages = enablePagination ? Math.ceil(cardItems.length / itemsPerPage) : 1;
  const validCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = enablePagination ? (validCurrentPage - 1) * itemsPerPage : 0;
  const endIndex = enablePagination ? startIndex + itemsPerPage : cardItems.length;
  let T0;
  let goToPage;
  let t6;
  let t7;
  let t8;
  if ($[3] !== cardBackgroundColor || $[4] !== cardItems || $[5] !== endIndex || $[6] !== itemWidthClass || $[7] !== pageParamKey || $[8] !== router || $[9] !== searchParams || $[10] !== startIndex) {
    const displayedItems = cardItems.slice(startIndex, endIndex);
    let t9;
    if ($[16] !== pageParamKey || $[17] !== router || $[18] !== searchParams) {
      t9 = page => {
        const params = new URLSearchParams(searchParams.toString());
        if (page === 1) {
          params.delete(pageParamKey);
        } else {
          params.set(pageParamKey, page.toString());
        }
        const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
        router.push(newUrl, {
          scroll: false
        });
        if (gridRef.current) {
          gridRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      };
      $[16] = pageParamKey;
      $[17] = router;
      $[18] = searchParams;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    goToPage = t9;
    T0 = Box;
    t6 = gridRef;
    t7 = "flex flex-wrap justify-center gap-8";
    let t10;
    if ($[20] !== cardBackgroundColor || $[21] !== itemWidthClass) {
      t10 = item => <Box key={item._id} className={itemWidthClass}><ReferenceCard item={item} cardBackgroundColor={cardBackgroundColor} /></Box>;
      $[20] = cardBackgroundColor;
      $[21] = itemWidthClass;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t8 = displayedItems.map(t10);
    $[3] = cardBackgroundColor;
    $[4] = cardItems;
    $[5] = endIndex;
    $[6] = itemWidthClass;
    $[7] = pageParamKey;
    $[8] = router;
    $[9] = searchParams;
    $[10] = startIndex;
    $[11] = T0;
    $[12] = goToPage;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    T0 = $[11];
    goToPage = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[23] !== T0 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t9 = <T0 ref={t6} className={t7}>{t8}</T0>;
    $[23] = T0;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== enablePagination || $[29] !== goToPage || $[30] !== totalPages || $[31] !== validCurrentPage) {
    t10 = enablePagination && totalPages > 1 && <PaginationControls currentPage={validCurrentPage} totalPages={totalPages} onPageChange={goToPage} className="mt-12" />;
    $[28] = enablePagination;
    $[29] = goToPage;
    $[30] = totalPages;
    $[31] = validCurrentPage;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[33] = t10;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
const ReferenceGridClient = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<ReferenceGridFallback {...props} />}><ReferenceGridClientInner {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

// Fallback component that renders the first page without pagination controls
const ReferenceGridFallback = t0 => {
  const $ = _c(13);
  const {
    cardItems,
    cols: t1,
    cardBackgroundColor,
    enablePagination: t2,
    itemsPerPage: t3
  } = t0;
  const cols = t1 === undefined ? 3 : t1;
  const enablePagination = t2 === undefined ? false : t2;
  const itemsPerPage = t3 === undefined ? 12 : t3;
  let t4;
  if ($[0] !== cardItems || $[1] !== enablePagination || $[2] !== itemsPerPage) {
    t4 = enablePagination ? cardItems.slice(0, itemsPerPage) : cardItems;
    $[0] = cardItems;
    $[1] = enablePagination;
    $[2] = itemsPerPage;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const displayedItems = t4;
  const itemWidthClass = ITEM_WIDTH_MAP[cols] || ITEM_WIDTH_MAP[3];
  let t5;
  if ($[4] !== cardBackgroundColor || $[5] !== displayedItems || $[6] !== itemWidthClass) {
    let t6;
    if ($[8] !== cardBackgroundColor || $[9] !== itemWidthClass) {
      t6 = item => <Box key={item._id} className={itemWidthClass}><ReferenceCard item={item} cardBackgroundColor={cardBackgroundColor} /></Box>;
      $[8] = cardBackgroundColor;
      $[9] = itemWidthClass;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = displayedItems.map(t6);
    $[4] = cardBackgroundColor;
    $[5] = displayedItems;
    $[6] = itemWidthClass;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <Box className="flex flex-wrap justify-center gap-8">{t5}</Box>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export default ReferenceGridClient;